import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Col, Form, Row, Select } from 'antd';
import { Typography } from '@hulu/react-style-components';
import { connect } from 'dva';
import { SearchOutlined } from '@ant-design/icons';
import { kebabCaseToSentenceCase, MomentDatePicker } from '../../../utils';
import {
  VASTTRACKER_SEARCH_DATE,
  VASTTRACKER_SEARCH_ID,
  VASTTRACKER_SEARCH_ID_BUTTON,
  VASTTRACKER_SEARCH_TITLE,
} from '../../../testUtils';
import moment from 'moment';
import { EMPTY_VALUES, SELECT_MODE, COUNT_TYPES, ALLOWED_APPS } from '../../../constants';

const FilterSearch = ({
  dispatch,
  countTypeSelected = 'N/A',
  selectMode = SELECT_MODE.TAGS,
  selectOptions = [],
  startDate,
  endDate,
  permissions,
}) => {
  const { Option } = Select;
  const [selectedApps, setSelectedApps] = useState([]);
  const [form] = Form.useForm();
  const refreshSearchIds = (data) => {
    const ids = data[countTypeSelected] ?? [];
    dispatch({ type: 'vastTracker/updateIds', payload: { ids, selectedApps } });
    dispatch({ type: 'vastTracker/pageInit' });
  };
  const refreshDate = (startDate, endDate) => {
    dispatch({ type: 'vastTracker/updateDates', payload: { startDate, endDate } });
    dispatch({ type: 'vastTracker/pageInit' });
  };
  const { RangePicker } = MomentDatePicker;

  useEffect(() => {
    return () => {
      // reset when unmounting
      dispatch({ type: 'vastTracker/resetToInitialState' });
      form.resetFields();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appSelectOptions = Object.entries(ALLOWED_APPS)
    .filter(([appKey]) => !permissions || permissions.can('ACCESS_APP', 'ALL') || permissions.can('ACCESS_APP', appKey))
    .map(([appKey, appLabel]) => ({
      value: appKey,
      label: appLabel,
    }));
  return (
    <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
      <Typography
        style={{ color: '#090699', marginBottom: '20px' }}
        variant="h5"
        data-testid={VASTTRACKER_SEARCH_TITLE}
      >
        Filter Counts
      </Typography>

      <Form form={form} onFinish={refreshSearchIds} style={{ marginBottom: '24px' }}>
        {countTypeSelected === COUNT_TYPES.ADUNITID && (
          <div
            style={{
              marginBottom: '20px',
              padding: '12px',
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: '1px solid #e8e8e8',
            }}
          >
            <Typography variant="h8" style={{ marginBottom: '8px', display: 'block', fontWeight: '600' }}>
              Filter by App
            </Typography>
            <Form.Item name="selectedApps" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                allowClear
                style={{ width: '100%' }}
                placeholder="Select Apps"
                value={selectedApps}
                onChange={setSelectedApps}
              >
                {appSelectOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    <Checkbox checked={selectedApps.includes(option.value)}>{option.label}</Checkbox>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}

        <div
          style={{
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: '#fff',
            borderRadius: '6px',
            border: '1px solid #e8e8e8',
          }}
        >
          <Typography variant="h8" style={{ marginBottom: '8px', display: 'block', fontWeight: '600' }}>
            Search By {kebabCaseToSentenceCase(countTypeSelected)}
          </Typography>
          <Form.Item name={countTypeSelected} style={{ marginBottom: '12px' }}>
            <Select
              allowClear
              mode={selectMode}
              notFoundContent={EMPTY_VALUES}
              style={{ width: '100%' }}
              placeholder={`Select ${kebabCaseToSentenceCase(countTypeSelected)}`}
              data-testid={VASTTRACKER_SEARCH_ID}
              options={selectOptions}
              optionFilterProp={'label'}
            />
          </Form.Item>
          <Button
            htmlType="submit"
            icon={<SearchOutlined />}
            type="primary"
            style={{ width: '100%' }}
            data-testid={VASTTRACKER_SEARCH_ID_BUTTON}
          >
            Search
          </Button>
        </div>
      </Form>

      <div style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e8e8e8' }}>
        <Typography variant="h8" style={{ marginBottom: '8px', display: 'block', fontWeight: '600' }}>
          Show Data By Date (UTC)
        </Typography>
        <RangePicker
          allowEmpty={[false, false]}
          defaultValue={[startDate, endDate]}
          onChange={(dates) => dates && refreshDate(dates[0], dates[1])}
          style={{ width: '100%' }}
          ranges={{
            Today: [moment().startOf('day'), moment().endOf('day')],
            'Last 2 Days': [moment().startOf('day').subtract(1, 'day'), moment().endOf('day')],
            'Last 7 Days': [moment().startOf('day').subtract(6, 'day'), moment().endOf('day')],
            'Last 30 Days': [moment().startOf('day').subtract(29, 'day'), moment().endOf('day')],
          }}
          disabledDate={(date) => date < moment().subtract(1, 'month')}
          data-testid={VASTTRACKER_SEARCH_DATE}
        />
      </div>
    </div>
  );
};

function mapStateToProps({ vastTracker: { countTypeSelected, filters, startDate, endDate }, app: { currentUser } }) {
  return {
    countTypeSelected: countTypeSelected,
    selectMode: filters[countTypeSelected].selectMode,
    selectOptions: filters[countTypeSelected].options,
    startDate,
    endDate,
    permissions: currentUser.permissions,
  };
}

export default connect(mapStateToProps)(FilterSearch);
